import { collection, doc, getDoc, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';

// TypeScript interfaces
export interface SevenDayCampaignResult {
  uniqueCode: string;
  campaignType: string;
  impressions: number;
  clicks: number;
  spend: number;
  sales: number;
  orders: number;
  units: number;
}

export interface SevenDayCampaignData {
  asin: string;
  campaigns: SevenDayCampaignResult[];
  lastUpdated: Date;
}

/**
 * Get 7 Day Campaign Results data for a specific ASIN
 * @param companyName - The company name
 * @param asin - The ASIN to get data for
 * @returns Promise<SevenDayCampaignData | null>
 */
export async function getSevenDayCampaignResultsData(
  companyName: string, 
  asin: string
): Promise<SevenDayCampaignData | null> {
  try {
    const docRef = doc(db, `pmt2_7day_campaign_results/${companyName}/asins/${asin}`);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        asin: data.asin,
        campaigns: data.campaigns || [],
        lastUpdated: data.lastUpdated?.toDate() || new Date()
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching 7 Day Campaign Results data:', error);
    throw error;
  }
}

/**
 * Get all 7 Day Campaign Results data for a company
 * @param companyName - The company name
 * @returns Promise<SevenDayCampaignData[]>
 */
export async function getAllSevenDayCampaignResultsData(
  companyName: string
): Promise<SevenDayCampaignData[]> {
  try {
    const collectionRef = collection(db, `pmt2_7day_campaign_results/${companyName}/asins`);
    const q = query(collectionRef, orderBy('asin'));
    const querySnapshot = await getDocs(q);
    
    const results: SevenDayCampaignData[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      results.push({
        asin: data.asin,
        campaigns: data.campaigns || [],
        lastUpdated: data.lastUpdated?.toDate() || new Date()
      });
    });
    
    return results;
  } catch (error) {
    console.error('Error fetching all 7 Day Campaign Results data:', error);
    throw error;
  }
}

/**
 * Get 7 Day Campaign Results data for specific campaign types
 * @param companyName - The company name
 * @param asin - The ASIN to get data for
 * @param campaignTypes - Array of campaign types to filter by
 * @returns Promise<SevenDayCampaignResult[]>
 */
export async function getSevenDayCampaignResultsByCampaignType(
  companyName: string,
  asin: string,
  campaignTypes: string[]
): Promise<SevenDayCampaignResult[]> {
  try {
    const campaignData = await getSevenDayCampaignResultsData(companyName, asin);
    
    if (!campaignData) {
      return [];
    }
    
    return campaignData.campaigns.filter(campaign => 
      campaignTypes.includes(campaign.campaignType)
    );
  } catch (error) {
    console.error('Error fetching 7 Day Campaign Results by campaign type:', error);
    throw error;
  }
}

/**
 * Get aggregated metrics for all campaigns of an ASIN
 * @param companyName - The company name
 * @param asin - The ASIN to get data for
 * @returns Promise<SevenDayCampaignResult | null> - Aggregated totals
 */
export async function getAggregatedSevenDayCampaignResults(
  companyName: string,
  asin: string
): Promise<SevenDayCampaignResult | null> {
  try {
    const campaignData = await getSevenDayCampaignResultsData(companyName, asin);
    
    if (!campaignData || campaignData.campaigns.length === 0) {
      return null;
    }
    
    const aggregated = campaignData.campaigns.reduce(
      (acc, campaign) => ({
        uniqueCode: 'AGGREGATED',
        campaignType: 'All Campaigns',
        impressions: acc.impressions + campaign.impressions,
        clicks: acc.clicks + campaign.clicks,
        spend: acc.spend + campaign.spend,
        sales: acc.sales + campaign.sales,
        orders: acc.orders + campaign.orders,
        units: acc.units + campaign.units
      }),
      {
        uniqueCode: 'AGGREGATED',
        campaignType: 'All Campaigns',
        impressions: 0,
        clicks: 0,
        spend: 0,
        sales: 0,
        orders: 0,
        units: 0
      }
    );
    
    return aggregated;
  } catch (error) {
    console.error('Error calculating aggregated 7 Day Campaign Results:', error);
    throw error;
  }
}